import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useAuth } from "../../auth/AuthContext";
import { useDraggableDock } from "../../help/useDraggableDock";
import { Icon } from "../Icon";
import {
  clearFeedbackDraft,
  draftHasContent,
  emptyFeedbackDraft,
  FEEDBACK_DOCK_POSITION_KEY,
  loadFeedbackDraft,
  saveFeedbackDraft,
  type FeedbackDraft,
} from "./feedbackDraft";
import { FeedbackForm } from "./FeedbackForm";

type Panel = "closed" | "collapsed" | "expanded";

interface FeedbackDockValue {
  open: () => void;
  /** Bumped on every successful send, so a page can refresh its own list without a callback prop. */
  submitCount: number;
}

const FeedbackDockContext = createContext<FeedbackDockValue | null>(null);

export function useFeedbackDock(): FeedbackDockValue {
  const ctx = useContext(FeedbackDockContext);
  // A launcher rendered outside the provider would silently do nothing, which is worse than failing.
  if (!ctx) throw new Error("useFeedbackDock must be used inside <FeedbackDockProvider>");
  return ctx;
}

/**
 * The feedback dock: a floating, non-blocking panel that keeps what you typed.
 *
 * Ported from web-admin's `FeedbackDock.tsx` (KEY-327, hardened under KEY-380). The `Modal` it
 * replaces had one problem that mattered and one that made it worse. It covered the whole viewport,
 * including **the very thing being reported** — so a bug report had to be written from memory. And it
 * reset every field whenever it opened, so a stray Escape or click on the backdrop destroyed the draft
 * outright.
 *
 * Three deliberate choices follow from that:
 *
 * - **Mounted beside `<Routes>`, not inside a page.** State lives above the router, so navigating to
 *   look at the thing you're describing cannot unmount the form. This is the point of the feature.
 * - **Escape collapses, it never closes and never clears.** And the handler is on the panel element,
 *   not `window` — web-user's `Modal` already installs a document-level Escape listener, so a
 *   window-level handler here would close two things at once. The Cancel button is the one path to a
 *   real close (`panel === "closed"`), distinct from Escape and the header chevron's collapse.
 * - **Only sending, an explicit Discard, or signing out clears the draft.** Collapsing, navigating
 *   and reloading all keep it.
 */
export function FeedbackDockProvider({ children }: { children: ReactNode }) {
  const [panel, setPanel] = useState<Panel>("closed");
  const [draft, setDraft] = useState<FeedbackDraft>(emptyFeedbackDraft);
  const [submitCount, setSubmitCount] = useState(0);
  const restored = useRef(false);

  // Restore once, on mount. A recovered draft comes back COLLAPSED rather than expanded: reopening
  // the app shouldn't shove a half-written report in front of whatever you actually came to do — the
  // "Draft" badge on the pill is enough to say it's still there.
  useEffect(() => {
    if (restored.current) return;
    restored.current = true;
    const stored = loadFeedbackDraft();
    if (stored && draftHasContent(stored)) {
      setDraft(stored);
      setPanel("collapsed");
    }
  }, []);

  // Debounced so typing doesn't write on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => saveFeedbackDraft(draft), 400);
    return () => clearTimeout(t);
  }, [draft]);

  const open = useCallback(() => setPanel("expanded"), []);
  const value = useMemo(() => ({ open, submitCount }), [open, submitCount]);

  return (
    <FeedbackDockContext.Provider value={value}>
      {children}
      <Dock
        panel={panel}
        setPanel={setPanel}
        draft={draft}
        setDraft={setDraft}
        onSent={() => {
          setDraft(emptyFeedbackDraft);
          clearFeedbackDraft();
          setSubmitCount((n) => n + 1);
        }}
      />
    </FeedbackDockContext.Provider>
  );
}

/** A small dot-grid grip, standing in for a drag handle — same glyph as the Help videos dock (KEY-393). */
function GripGlyph() {
  return (
    <svg width="12" height="18" viewBox="0 0 12 18" aria-hidden="true" style={{ flex: "none", color: "var(--text-faint)" }}>
      {[3, 9].flatMap((x) => [2, 9, 16].map((y) => <circle key={`${x}:${y}`} cx={x} cy={y} r="1.4" fill="currentColor" />))}
    </svg>
  );
}

function Dock({
  panel,
  setPanel,
  draft,
  setDraft,
  onSent,
}: {
  panel: Panel;
  setPanel: (p: Panel) => void;
  draft: FeedbackDraft;
  setDraft: (updater: (d: FeedbackDraft) => FeedbackDraft) => void;
  onSent: () => void;
}) {
  const { me } = useAuth();
  const { position, dragging, elementRef, handleProps, suppressClickAfterDrag } = useDraggableDock(FEEDBACK_DOCK_POSITION_KEY);

  // Never on the sign-in screen: there is nobody to attribute the feedback to yet.
  if (!me || panel === "closed") return null;

  const hasDraft = draftHasContent(draft);
  // Matches the Help videos dock's z-index (100) — web-user's floating panels all sit there, unlike
  // web-admin where 65 was chosen to stay under Modal/PreviewOverlay and over Drawer. web-user's Modal
  // and SlideOver both also use 100, so a real modal opened while the dock is expanded still wins by
  // stacking order (the dock is mounted first, so it paints first, and Escape is panel-scoped so it
  // never fights Modal's own handler either way).
  const zIndex = 100;
  const placement = position
    ? { left: position.x, top: position.y }
    : { right: 16, top: 76 };

  if (panel === "collapsed") {
    return (
      <div
        ref={elementRef}
        {...handleProps}
        // userSelect off on both drag surfaces: without it a drag highlights the label text as it
        // goes, which looks like the app misreading the gesture even though the drag itself works.
        style={{ position: "fixed", ...placement, zIndex, touchAction: "none", userSelect: "none", cursor: dragging ? "grabbing" : "grab" }}
      >
        <button
          type="button"
          // The pill is its own drag handle, so a drag that ends over it must not also toggle it.
          onClick={() => { if (!suppressClickAfterDrag()) setPanel("expanded"); }}
          className="kd-btn-primary"
          style={{ boxShadow: "var(--shadow-lg)" }}
        >
          <Icon name="chat" size={14} />
          Feedback
          {hasDraft && (
            <span
              style={{
                background: "rgba(255,255,255,.22)",
                color: "inherit",
                fontSize: 10.5,
                fontWeight: 700,
                padding: "1px 6px",
                borderRadius: 99,
              }}
            >
              Draft
            </span>
          )}
        </button>
      </div>
    );
  }

  return (
    <div
      ref={elementRef}
      role="region"
      aria-label="Send feedback"
      // On the panel, not window: a window listener would also reach Modal/SlideOver and close two
      // things with one press. Escape collapses — it never discards what you typed.
      onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); setPanel("collapsed"); } }}
      style={{
        position: "fixed",
        ...placement,
        zIndex,
        width: 380,
        maxWidth: "calc(100vw - 32px)",
        maxHeight: "calc(100vh - 96px)",
        display: "flex",
        flexDirection: "column",
        borderRadius: "var(--radius-lg)",
        border: "var(--border-w) solid var(--border)",
        background: "var(--surface)",
        boxShadow: "var(--shadow-lg)",
      }}
    >
      <div
        {...handleProps}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "14px 12px 14px 16px",
          borderBottom: "var(--border-w) solid var(--border)",
          touchAction: "none",
          userSelect: "none",
          cursor: dragging ? "grabbing" : "grab",
        }}
      >
        <GripGlyph />
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: "var(--text)", flex: 1 }}>Send feedback</h2>
        {hasDraft && (
          <button
            type="button"
            title="Discard this draft"
            aria-label="Discard this feedback draft"
            onClick={() => {
              // The one place a draft is thrown away on purpose, so it asks — the whole feature
              // exists because losing one by accident was the original complaint.
              if (window.confirm("Discard this feedback draft?")) {
                setDraft(() => emptyFeedbackDraft);
                clearFeedbackDraft();
              }
            }}
            style={{ border: "none", background: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 12, fontWeight: 600, padding: "2px 4px" }}
          >
            Discard
          </button>
        )}
        <button
          type="button"
          onClick={() => setPanel("collapsed")}
          aria-label="Collapse the feedback panel"
          title="Collapse"
          style={{
            width: 32,
            height: 32,
            borderRadius: 99,
            border: "none",
            background: "var(--surface-inset)",
            color: "var(--text-muted)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: "none",
          }}
        >
          <Icon name="chevDown" size={16} />
        </button>
      </div>

      <div style={{ padding: 14, overflowY: "auto" }}>
        <FeedbackForm
          draft={draft}
          onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))}
          onSent={onSent}
          onCancel={() => setPanel("closed")}
          cancelLabel="Close"
        />
      </div>
    </div>
  );
}
